<template>
    <tbody
        v-bind:class="{
            'edit-mode': editing,
            'history-showing': isShowingHistoryDetails,
        }"
    >
        <template v-if="!editing">
            <tr :class="{ success: updatedRates[rate.id] }">
                <td>
                    <a
                        href="#"
                        v-on:click="handleBeginHistory"
                        v-show="showingHistory && !isShowingHistoryDetails"
                    >
                        <span class="glyphicon glyphicon-chevron-right"></span>
                    </a>
                    <a
                        href="#"
                        v-on:click="handleHideHistory"
                        v-show="showingHistory && isShowingHistoryDetails"
                    >
                        <span class="glyphicon glyphicon-chevron-down"></span>
                    </a>
                    {{ rateType }}
                </td>
                <td>
                    <span v-if="rate.market_group">{{
                        rate.market_group.name
                    }}</span>
                    <em v-else class="text-muted">Unknown</em>
                </td>
                <td>{{ expirationDate }}</td>
                <td>
                    <template
                        v-if="
                            rate.is_sponsored_rate &&
                            featureFlag('home-equity.sponsored_rate')
                        "
                    >
                        <em class="text-muted">N/A</em>
                    </template>
                    <template v-else>
                        <span v-if="rate.rate !== null">{{ rate.rate }}%</span>
                        <em v-else class="text-muted">N/A</em>
                    </template>
                </td>
                <td>
                    <template
                        v-if="
                            rate.is_sponsored_rate &&
                            featureFlag('home-equity.sponsored_rate')
                        "
                    >
                        <em class="text-muted">N/A</em>
                    </template>
                    <template v-else>
                        <span v-if="rate.intro_term !== null"
                            >{{ rate.intro_term }} Mos.</span
                        >
                        <em v-else class="text-muted">N/A</em>
                    </template>
                </td>
                <td>
                    <template
                        v-if="
                            rate.is_sponsored_rate &&
                            featureFlag('home-equity.sponsored_rate')
                        "
                    >
                        <em class="text-muted">N/A</em>
                    </template>
                    <template v-else>{{ rate.post_intro_rate }}%</template>
                </td>
                <td>
                    <template
                        v-if="
                            rate.is_sponsored_rate &&
                            featureFlag('home-equity.sponsored_rate')
                        "
                    >
                        <em class="text-muted">N/A</em>
                    </template>
                    <template v-else>
                        <span v-if="rate.term !== null"
                            >{{ termInYears }} Yrs.</span
                        >
                        <em v-else class="text-muted">N/A</em>
                    </template>
                </td>
                <td>${{ rate.loan_amount_min }}</td>
                <td>${{ rate.loan_amount_max }}</td>
                <td>
                    <template
                        v-if="
                            rate.is_sponsored_rate &&
                            featureFlag('home-equity.sponsored_rate')
                        "
                    >
                        <em class="text-muted">N/A</em>
                    </template>
                    <template v-else>
                        <template v-if="rate.fee_type === 'percent'"
                            >{{ rate.fee_amount }}%</template
                        >
                        <template v-else>${{ rate.fee_amount }}</template>
                    </template>
                </td>
                <td>
                    <template
                        v-if="
                            rate.is_sponsored_rate &&
                            featureFlag('home-equity.sponsored_rate')
                        "
                    >
                        <em class="text-muted">N/A</em>
                    </template>
                    <template v-else>${{ rate.draw_amount }}</template>
                </td>
                <td>{{ rate.fico_min }}</td>
                <td>{{ rate.fico_max }}</td>
                <td>{{ rate.ltv_min }}%</td>
                <td>{{ rate.ltv_max }}%</td>
                <td
                    v-show="!showingHistory"
                    class="text-center"
                    v-if="featureFlag('home-equity.sponsored_rate')"
                >
                    <span v-if="rate.is_sponsored_rate">Yes</span>
                    <span v-else>No</span>
                </td>
                <td v-show="showingHistory">
                    <abbr :title="rate.description">{{ rate.updated_at }}</abbr>
                </td>
                <td v-show="showingHistory">
                    <span v-if="rate.is_old">Prior</span>
                    <span v-else>{{ rate.activity }}</span>
                </td>
                <td v-show="!showingHistory">
                    <a
                        :href="
                            '/campaigns/home-equity/rates/' + rate.id + '/show'
                        "
                    >
                        <span class="glyphicon glyphicon-eye-open"></span>
                    </a>
                </td>
                <td v-show="!showingHistory">
                    <a href="#" v-on:click="handleBeginEdit">
                        <span class="glyphicon glyphicon-pencil"></span>
                    </a>
                </td>
                <td v-show="!showingHistory" style="padding: 0">
                    <div
                        class="hidden-xs hidden-sm hidden-md"
                        style="padding: 8px"
                    >
                        <a
                            :href="
                                '/campaigns/home-equity/rates/' +
                                rate.id +
                                '/delete'
                            "
                            class="text-danger"
                        >
                            <span class="glyphicon glyphicon-trash"></span>
                        </a>
                    </div>
                </td>
            </tr>
        </template>
        <template v-else>
            <tr class="edit-mode">
                <td :class="{ hasErrors: hasErrors('type') }">
                    <rate-field-error
                        :errors="getErrors('type')"
                        v-if="hasErrors('type')"
                    ></rate-field-error>
                    <select
                        v-model="editingRate.type"
                        class="form-control input-sm"
                        :disabled="disabled"
                    >
                        <option value>--</option>
                        <option
                            v-for="(typeName, typeKey) in types"
                            v-bind:value="typeKey"
                            v-bind:key="typeKey"
                        >
                            {{ typeName }}
                        </option>
                    </select>
                </td>
                <td :class="{ hasErrors: hasErrors('market_group_id') }">
                    <rate-field-error
                        :errors="getErrors('market_group_id')"
                        v-if="hasErrors('market_group_id')"
                    ></rate-field-error>
                    <select
                        v-model="editingRate.market_group_id"
                        class="form-control input-sm"
                        :disabled="disabled"
                    >
                        <option value>--</option>
                        <option
                            v-for="group in groups"
                            v-bind:value="group.id"
                            v-bind:key="group.id"
                        >
                            {{ group.name }}
                        </option>
                    </select>
                </td>
                <td :class="{ hasErrors: hasErrors('expiration_date') }">
                    <rate-field-error
                        :errors="getErrors('expiration_date')"
                        v-if="hasErrors('expiration_date')"
                    ></rate-field-error>
                    <input
                        v-model="editingRate.expirationDate"
                        type="date"
                        class="form-control input-sm"
                        style="max-width: 126px"
                        :disabled="disabled"
                    />
                </td>
                <td :class="{ hasErrors: hasErrors('rate') }">
                    <rate-field-error
                        :errors="getErrors('rate')"
                        v-if="hasErrors('rate')"
                    ></rate-field-error>
                    <percent-input
                        v-if="!editingRate.is_sponsored_rate"
                        v-model="editingRate.rate"
                        :precision="3"
                        :disabled="disabled"
                    ></percent-input>
                    <input type="text" size="5" disabled v-else />
                </td>
                <td :class="{ hasErrors: hasErrors('intro_term') }">
                    <rate-field-error
                        v-if="hasErrors('intro_term')"
                        :errors="getErrors('intro_term')"
                    ></rate-field-error>
                    <months-input
                        v-if="!editingRate.is_sponsored_rate"
                        v-model="editingRate.intro_term"
                        :disabled="disabled"
                    ></months-input>
                    <input type="text" size="5" disabled v-else />
                </td>
                <td :class="{ hasErrors: hasErrors('post_intro_rate') }">
                    <rate-field-error
                        :errors="getErrors('post_intro_rate')"
                        v-if="hasErrors('post_intro_rate')"
                    ></rate-field-error>
                    <percent-input
                        v-if="!editingRate.is_sponsored_rate"
                        v-model="editingRate.post_intro_rate"
                        :precision="3"
                        :disabled="disabled"
                    ></percent-input>
                    <input type="text" size="5" disabled v-else />
                </td>
                <td :class="{ hasErrors: hasErrors('term_in_years') }">
                    <rate-field-error
                        v-if="hasErrors('term_in_years')"
                        :errors="getErrors('term_in_years')"
                    ></rate-field-error>
                    <years-input
                        v-if="!editingRate.is_sponsored_rate"
                        v-model="editingRate.termInYears"
                        :disabled="disabled"
                    ></years-input>
                    <input type="text" size="5" disabled v-else />
                </td>
                <td :class="{ hasErrors: hasErrors('loan_amount_min') }">
                    <rate-field-error
                        :errors="getErrors('loan_amount_min')"
                        v-if="hasErrors('loan_amount_min')"
                    ></rate-field-error>
                    <dollar-input
                        v-model="editingRate.loan_amount_min"
                        :include-cents="false"
                        :disabled="disabled"
                    ></dollar-input>
                </td>
                <td :class="{ hasErrors: hasErrors('loan_amount_max') }">
                    <rate-field-error
                        :errors="getErrors('loan_amount_max')"
                        v-if="hasErrors('loan_amount_max')"
                    ></rate-field-error>
                    <dollar-input
                        v-model="editingRate.loan_amount_max"
                        :include-cents="false"
                        :disabled="disabled"
                    ></dollar-input>
                </td>
                <td :class="{ hasErrors: hasErrors('fee_amount') }">
                    <template v-if="editingRate.is_sponsored_rate">
                        <input type="text" size="5" disabled />
                    </template>
                    <template v-else>
                        <percent-input
                            v-if="editingRate.fee_type === 'percent'"
                            v-model="editingRate.fee_amount"
                            :precision="2"
                            :disabled="disabled"
                        ></percent-input>
                        <dollar-input
                            v-else
                            v-model="editingRate.fee_amount"
                            :disabled="disabled"
                        ></dollar-input>
                    </template>
                </td>
                <td :class="{ hasErrors: hasErrors('draw_amount') }">
                    <rate-field-error
                        :errors="getErrors('draw_amount')"
                        v-if="hasErrors('draw_amount')"
                    ></rate-field-error>
                    <dollar-input
                        v-if="!editingRate.is_sponsored_rate"
                        v-model="editingRate.draw_amount"
                        :include-cents="false"
                        :disabled="disabled"
                    ></dollar-input>
                    <input type="text" size="5" disabled v-else />
                </td>
                <td
                    :class="{ hasErrors: hasErrors('fico_min', 'fico_ranges') }"
                >
                    <rate-field-error
                        :errors="getErrors('fico_min', 'fico_ranges')"
                        v-if="hasErrors('fico_min', 'fico_ranges')"
                    ></rate-field-error>
                    <select
                        v-model="editingRate.fico_min"
                        class="fico-select input-sm form-control"
                        :disabled="disabled"
                    >
                        <option value>--</option>
                        <option
                            v-for="v in ficoMinData"
                            v-bind:value="v"
                            v-bind:key="v"
                        >
                            {{ v }}
                        </option>
                    </select>
                </td>
                <td
                    :class="{ hasErrors: hasErrors('fico_max', 'fico_ranges') }"
                >
                    <rate-field-error
                        :errors="getErrors('fico_max')"
                        v-if="hasErrors('fico_max')"
                    ></rate-field-error>
                    <select
                        v-model="editingRate.fico_max"
                        class="fico-select input-sm form-control"
                        :disabled="disabled"
                    >
                        <option value>--</option>
                        <option
                            v-for="v in ficoMaxData"
                            v-bind:value="v"
                            v-bind:key="v"
                        >
                            {{ v }}
                        </option>
                    </select>
                </td>
                <td :class="{ hasErrors: hasErrors('ltv_min') }">
                    <rate-field-error
                        :errors="getErrors('ltv_min')"
                        v-if="hasErrors('ltv_min')"
                    ></rate-field-error>
                    <percent-input
                        v-model="editingRate.ltv_min"
                        :precision="2"
                        :disabled="disabled"
                    ></percent-input>
                </td>
                <td :class="{ hasErrors: hasErrors('ltv_max') }">
                    <rate-field-error
                        :errors="getErrors('ltv_max')"
                        v-if="hasErrors('ltv_max')"
                    ></rate-field-error>
                    <percent-input
                        v-model="editingRate.ltv_max"
                        :precision="2"
                        :disabled="disabled"
                    ></percent-input>
                </td>
                <td
                    :class="{ hasErrors: hasErrors('is_sponsored_rate') }"
                    v-if="featureFlag('home-equity.sponsored_rate')"
                >
                    <rate-field-error
                        :errors="getErrors('is_sponsored_rate')"
                        v-if="hasErrors('is_sponsored_rate')"
                    ></rate-field-error>
                    <input
                        type="checkbox"
                        v-model="editingRate.is_sponsored_rate"
                        :checked="editingRate.is_sponsored_rate"
                    />
                </td>
                <td>
                    <a
                        href="#"
                        v-if="!disabled"
                        v-on:click="handleCancelEdit"
                        class="text-danger"
                    >
                        <span class="glyphicon glyphicon-remove"></span>
                    </a>
                    <em v-else class="text-muted">
                        <span class="glyphicon glyphicon-remove"></span>
                    </em>
                </td>
                <td>
                    <rate-field-error
                        :errors="getErrors('id', 'rate_overlap')"
                        :min-width="'200px'"
                        :alignment="'right'"
                    ></rate-field-error>
                    <a
                        href="#"
                        v-if="!disabled"
                        v-on:click="handleSaveEdit"
                        class="text-success"
                    >
                        <span class="glyphicon glyphicon-ok"></span>
                    </a>
                    <em v-else class="text-muted">
                        <span class="glyphicon glyphicon-ok"></span>
                    </em>
                </td>
                <td style="padding: 0">
                    <div
                        class="hidden-xs hidden-sm hidden-md"
                        style="padding: 8px"
                    >
                        <em class="text-muted">
                            <span class="glyphicon glyphicon-trash"></span>
                        </em>
                    </div>
                </td>
            </tr>
            <tr class="edit-mode">
                <td colspan="19">
                    <div class="row">
                        <div class="col-lg-2">
                            <template
                                v-if="
                                    editingRate.is_sponsored_rate &&
                                    featureFlag('home-equity.sponsored_rate')
                                "
                            >
                                <em class="text-muted"
                                    >The fees aren't available because this is a
                                    sponsored rate.</em
                                >
                            </template>
                            <template v-else>
                                <div class="row">
                                    <div class="col-xs-7">
                                        <label>Origination Fee</label>
                                    </div>
                                    <div
                                        class="col-xs-5"
                                        :class="{
                                            hasErrors:
                                                hasErrors('fee_origination'),
                                        }"
                                    >
                                        <percent-input
                                            v-if="
                                                editingRate.fee_origination_type ===
                                                'percent'
                                            "
                                            v-model="
                                                editingRate.fee_origination
                                            "
                                            :precision="2"
                                            :disabled="disabled"
                                        ></percent-input>
                                        <dollar-input
                                            v-else
                                            v-model="
                                                editingRate.fee_origination
                                            "
                                            :disabled="disabled"
                                        ></dollar-input>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-xs-7">
                                        <label>Annual Fee</label>
                                    </div>
                                    <div
                                        class="col-xs-5"
                                        :class="{
                                            hasErrors: hasErrors('fee_annual'),
                                        }"
                                    >
                                        <percent-input
                                            v-if="
                                                editingRate.annual_fee_type ===
                                                'percent'
                                            "
                                            v-model="editingRate.fee_annual"
                                            :precision="2"
                                            :disabled="disabled"
                                        ></percent-input>
                                        <dollar-input
                                            v-else
                                            v-model="editingRate.fee_annual"
                                            :disabled="disabled"
                                        ></dollar-input>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-xs-7">
                                        <label>Application Fee</label>
                                    </div>
                                    <div
                                        class="col-xs-5"
                                        :class="{
                                            hasErrors:
                                                hasErrors('fee_application'),
                                        }"
                                    >
                                        <percent-input
                                            v-if="
                                                editingRate.application_fee_type ===
                                                'percent'
                                            "
                                            v-model="
                                                editingRate.fee_application
                                            "
                                            :precision="2"
                                            :disabled="disabled"
                                        ></percent-input>
                                        <dollar-input
                                            v-else
                                            v-model="
                                                editingRate.fee_application
                                            "
                                            :disabled="disabled"
                                        ></dollar-input>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-xs-7">
                                        <label>Membership Fee</label>
                                    </div>
                                    <div
                                        class="col-xs-5"
                                        :class="{
                                            hasErrors:
                                                hasErrors('fee_membership'),
                                        }"
                                    >
                                        <percent-input
                                            v-if="
                                                editingRate.membership_fee_type ===
                                                'percent'
                                            "
                                            v-model="editingRate.fee_membership"
                                            :precision="2"
                                            :disabled="disabled"
                                        ></percent-input>
                                        <dollar-input
                                            v-else
                                            v-model="editingRate.fee_membership"
                                            :disabled="disabled"
                                        ></dollar-input>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-xs-7">
                                        <label>Underwriting Fee</label>
                                    </div>
                                    <div
                                        class="col-xs-5"
                                        :class="{
                                            hasErrors:
                                                hasErrors('fee_underwriting'),
                                        }"
                                    >
                                        <percent-input
                                            v-if="
                                                editingRate.underwriting_fee_type ===
                                                'percent'
                                            "
                                            v-model="
                                                editingRate.fee_underwriting
                                            "
                                            :precision="2"
                                            :disabled="disabled"
                                        ></percent-input>
                                        <dollar-input
                                            v-else
                                            v-model="
                                                editingRate.fee_underwriting
                                            "
                                            :disabled="disabled"
                                        ></dollar-input>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-xs-7">
                                        <label>Other Fee</label>
                                    </div>
                                    <div
                                        class="col-xs-5"
                                        :class="{
                                            hasErrors: hasErrors('fee_other'),
                                        }"
                                    >
                                        <percent-input
                                            v-if="
                                                editingRate.other_fee_type ===
                                                'percent'
                                            "
                                            v-model="editingRate.fee_other"
                                            :precision="2"
                                            :disabled="disabled"
                                        ></percent-input>
                                        <dollar-input
                                            v-else
                                            v-model="editingRate.fee_other"
                                            :disabled="disabled"
                                        ></dollar-input>
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div class="col-lg-5">
                            <rate-field-error
                                :errors="getErrors('fee_conditions')"
                            ></rate-field-error>
                            <label>Fees and Conditions</label>
                            <vue-quill-editor
                                v-model="editingRate.fee_conditions"
                            ></vue-quill-editor>
                        </div>
                        <div class="col-lg-5">
                            <rate-field-error
                                :errors="getErrors('internal_notes')"
                            ></rate-field-error>
                            <label>Internal Notes</label>
                            <vue-quill-editor
                                v-model="editingRate.internal_notes"
                            ></vue-quill-editor>
                        </div>
                    </div>
                </td>
            </tr>
        </template>
        <template v-if="isShowingHistoryDetails" v-for="rate in historyData">
            <rate-history-row
                v-bind:rate="rate"
                v-bind:key="rate.id + '_data'"
            ></rate-history-row>
            <rate-history-text-row
                v-bind:rate="rate"
                v-bind:key="rate.id + '_text'"
            ></rate-history-text-row>
        </template>
    </tbody>
</template>

<style lang="scss">
.table.rate-table {
    input,
    select {
        color: #000;
    }
    > {
        tbody.edit-mode,
        tbody.history-showing {
            border-bottom: 2px solid #aaa;
        }
        tbody:nth-of-type(even) > tr {
            background-color: #ffffff;
        }
        tbody:nth-of-type(odd) > tr {
            background-color: #f9f9f9;
        }
        tbody + tbody {
            border-top: 0;
        }
    }
    tbody {
        .rate-history-row {
            td:first-child {
                border-left: 2px solid #337ab7;
            }
            .field-updated {
                background-color: #ffcccc;
            }
        }
        .rate-history-row-data + .rate-history-row-text {
            td {
                border-top-color: #eee;
            }
        }
    }
    input[type="date"].form-control.input-sm {
        height: 27px;
        padding: 4px;
        font-size: 12px;
        line-height: 1.5;
    }
    select.input-sm.form-control {
        height: 27px;
        padding: 4px;
        font-size: 12px;
        line-height: 1.5;
        border-radius: 0px;
    }
    .fico-select {
        min-width: 50px;
    }
    .quill-editor-textarea {
        background-color: #ffffff;
    }
    .hasErrors {
        background-color: #ffcccc;
    }
    .text-muted {
        color: #aaa;
    }
}
</style>

<script type="text/babel">
import { mapActions, mapGetters } from "vuex";
import RateHistoryRow from "./rate-list-history-row.vue";
import RateHistoryTextRow from "./rate-list-history-text-row.vue";
import moment from "moment";
import store from "./store.js";
import DollarInput from "./dollar-input.vue";
import PercentInput from "./percent-input.vue";
import YearsInput from "./years-input.vue";
import MonthsInput from "./months-input.vue";
import RateFieldError from "./rate-field-error.vue";
import VueQuillEditor from "../../utils/vue-quill-editor";

export default {
    name: "rate-list-row",
    store: store,
    props: {
        rate: {
            type: Object,
        },
        editing: {
            type: Boolean,
        },
        showingHistory: {
            type: Boolean,
        },
    },
    data() {
        return {
            loading: false,
            editingRate: null,
            editingRateExpirationDate: null,
            isShowingHistoryDetails: false,
            historyData: [],
            editingErrors: null,
            ficoMinData: [
                "780",
                "770",
                "760",
                "750",
                "740",
                "730",
                "720",
                "710",
                "700",
                "690",
                "680",
                "670",
                "660",
                "650",
                "640",
                "630",
                "620",
                "610",
                "600",
                "590",
                "580",
                "570",
                "560",
                "550",
                "540",
                "530",
                "520",
                "510",
                "500",
            ],
            ficoMaxData: [
                "850",
                "779",
                "769",
                "759",
                "749",
                "739",
                "729",
                "719",
                "709",
                "699",
                "689",
                "679",
                "669",
                "659",
                "649",
                "639",
                "629",
                "619",
                "609",
                "599",
                "589",
                "579",
                "569",
                "559",
                "549",
                "539",
                "529",
                "519",
                "509",
            ],
        };
    },
    watch: {
        editing(newEditing, oldEditing) {
            if (newEditing) {
                this.initEditForm();
            } else {
                this.editingRate = null;
                this.editingErrors = null;
            }
        },
    },
    computed: {
        ...mapGetters([
            "types",
            "getTypes",
            "groups",
            "rateHistory",
            "updatedRates",
        ]),
        expirationDate() {
            return moment(this.rate.expiration_date).format("MM/DD/YYYY");
        },
        rateType() {
            return this.rate.type.replace(/-/g, " ");
        },
        termInYears() {
            if (this.rate.term === null) {
                return null;
            }
            return Math.floor(this.rate.term / 12);
        },
        disabled() {
            return this.loading;
        },
    },
    methods: {
        ...mapActions(["handleRateUpdates", "beginEditMode", "endEditMode"]),
        initEditForm() {
            this.editingRate = Object.assign({}, this.rate); //copy rate b/c i dont want to be messing with the active rate reference.
            this.editingRate.termInYears = this.termInYears;
            this.editingRate.expirationDate = moment(
                this.rate.expiration_date
            ).format("YYYY-MM-DD");
        },
        handleBeginHistory(e) {
            e.preventDefault();
            this.loading = true;
            this.isShowingHistoryDetails = true;
            this.getHistoryDetails(this.rate.id);
        },
        getHistoryDetails(rateId) {
            axios
                .get("/api/home-equity/rates/" + rateId + "/history")
                .then((response) => {
                    this.loading = false;
                    this.historyData = response.data.rates;
                })
                .catch(() => {
                    this.loading = false;
                });
        },
        handleHideHistory(e) {
            e.preventDefault();
            this.isShowingHistoryDetails = false;
        },
        handleBeginEdit(e) {
            e.preventDefault();
            this.beginEditMode(this.rate.id);
        },
        handleCancelEdit(e) {
            e.preventDefault();
            this.endEditMode(this.rate.id);
        },
        handleSaveEdit(e) {
            e.preventDefault();
            // do save here
            this.loading = true;
            let postData = Object.assign({}, this.editingRate);
            postData.expiration_date = this.editingRate.expirationDate;
            if (
                this.editingRate.type === this.getTypes.HELOC &&
                (!this.editingRate.termInYears ||
                    this.editingRate.termInYears === "0")
            ) {
                // if term is blank or the string '0', set to null for heloc
                postData.term_in_years = null;
            } else {
                postData.term_in_years = this.editingRate.termInYears;
            }
            if (
                this.editingRate.type === this.getTypes.HELOC &&
                (!this.editingRate.intro_term ||
                    this.editingRate.intro_term === "0")
            ) {
                // if term is blank or the string '0', set to null for heloc
                postData.intro_term = null;
            } else {
                postData.intro_term = this.editingRate.intro_term;
            }
            postData.fico_ranges = [
                this.editingRate.fico_min + "|" + this.editingRate.fico_max,
            ];
            postData.rate = this.editingRate.is_sponsored_rate
                ? 0
                : this.editingRate.rate;
            postData.post_intro_rate = this.editingRate.is_sponsored_rate
                ? 0
                : this.editingRate.post_intro_rate;
            postData.term = this.editingRate.is_sponsored_rate
                ? null
                : this.editingRate.term;
            postData.fee_amount = this.editingRate.is_sponsored_rate
                ? 0
                : this.editingRate.fee_amount;
            postData.draw_amount = this.editingRate.is_sponsored_rate
                ? 0
                : this.editingRate.draw_amount;
            this.editingErrors = null;
            axios
                .put("/api/home-equity/rates/" + this.rate.id, postData)
                .then((response) => {
                    if (response.data.rates && response.data.rates.length > 0) {
                        this.handleRateUpdates(response.data.rates);
                    }
                    this.loading = false;
                    this.endEditMode(this.rate.id);
                })
                .catch((err) => {
                    if (err.response.status == 422) {
                        // validation errors
                        this.editingErrors =
                            err.response.data && err.response.data.errors;
                    } else if (
                        err.response.data.message &&
                        err.response.data.message
                    ) {
                        this.editingErrors = {
                            id: [err.response.data.message],
                        };
                    }
                    this.loading = false;
                });
        },
        fieldHasErrors(fieldname) {
            if (
                this.editingErrors === null ||
                typeof this.editingErrors[fieldname] === "undefined"
            ) {
                return false;
            }
            return true;
        },
        // because of how the api returns errors, we want to be able to easily accumulate errors
        // from multiple fields into a single array, especially because of how the requests are
        // organized.  this way we have a place we can put errors for fields that don't have
        // direct equivalents in the form.
        getErrors() {
            let fields = arguments;
            let errs = [];
            for (let i = 0, l = fields.length; i < l; i++) {
                if (this.fieldHasErrors(fields[i])) {
                    errs = errs.concat(this.editingErrors[fields[i]]);
                }
            }
            return errs;
        },
        // this behaves similarly to getErrors (in that you can give it multiple fields to check),
        // to simplify error style changes in the row
        hasErrors() {
            let fields = arguments;
            let errs = [];
            for (let i = 0, l = fields.length; i < l; i++) {
                if (this.fieldHasErrors(fields[i])) {
                    return true;
                }
            }
            return false;
        },
    },
    // we use beforeMount here because rates that may not be in the table when their editing flag is flipped still need to initialize before the form is rendered.
    beforeMount() {
        if (this.editing) {
            this.initEditForm();
        }
    },
    components: {
        RateHistoryRow,
        RateHistoryTextRow,
        DollarInput,
        PercentInput,
        YearsInput,
        MonthsInput,
        RateFieldError,
        VueQuillEditor,
    },
};
</script>
